import { GithubAuth, OAuth2 } from '@backstage/core-app-api';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  featureFlagsApiRef,
  githubAuthApiRef,
  oauthRequestApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import SignInIcon from '@material-ui/icons/AcUnit';
import { AdminToggleService, RestClient } from '@runway/devkit-common';
import { restClientRef, adminToggleServiceApiRef } from '../refs';
import ObserveService, { ObserveServiceApiRef } from './observe.service';
import { aaPingSSOAuthApiRef, MockGithubAuthApi, OAuth2AAPingSSO } from './runwayAuthApiRefs';
import AdService, { adServiceApiRef } from './ad.service';
import AzureManagementService, { AzureManagementServiceApiRef } from './am.service';
import {
  GithubEnterpriseClientApiGraphRef,
  GithubEnterpriseClientGraph,
  ProjectsDetails,
  projectsDetailsQueryApiRef,
} from './github.service';
import { UnifiedGithubService, unifiedGithubApiRef } from './unifiedGithubService';
import { emuAuthApiRef } from '../refs/emuAuth.ref';
import { EmuGithubAuthAdapter, MockEmuGithubAuthAdapter } from './emuAuth.service';
import Squad360Service, { Squad360ServiceApiRef } from './squad360.service';
import { NotificationService, NotificationServiceApiRef } from './notification.service';
import { SecretVaultService, SecretVaultServiceApiRef } from './secret-vault.service';
import ADOService, { ADOServiceApiRef } from './ado.service';
import ClusterService, { ClusterServiceApiRef } from './cluster-service.service';
import { AIProvisioningService, AIProvisioningServiceApiRef } from './AIProvisioning.service';
import { AIOneTrustService, AIOneTrustServiceApiRef } from './AIOneTrust.service';
import { AIAzureOnboardingService, AIAzureOnboardingServiceApiRef } from './AIAzureOnboarding.service';
import { AIResourcesDeletionService, AIResourcesDeletionServiceApiRef } from './AIResourcesDeletion.service';
import { OneTrustAssessmentService, OneTrustAssessmentServiceApiRef } from './OneTrustAssessment.service';
import { BackstageActionsApiRef, BackstageActionsService } from './backstageActions.service';

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: adminToggleServiceApiRef,
    deps: {
      configApi: configApiRef,
    },
    factory: ({ configApi }) => new AdminToggleService(configApi),
  }),
  createApiFactory({
    api: ClusterServiceApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, oAuthApi, restClient }) =>
      new ClusterService(configApi.getString('clusterService.baseUrl'), oAuthApi, restClient),
  }),
  createApiFactory({
    api: BackstageActionsApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) => new BackstageActionsService(discoveryApi, fetchApi),
  }),
  createApiFactory({
    api: restClientRef,
    deps: {},
    factory({}) {
      return new RestClient(console);
    },
  }),
  createApiFactory({
    api: ObserveServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, identityApi }) => new ObserveService(configApi.getString('backend.baseUrl'), identityApi),
  }),
  createApiFactory({
    api: aaPingSSOAuthApiRef,
    deps: {
      configApi: configApiRef,
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
      return new OAuth2AAPingSSO(
        OAuth2.create({
          discoveryApi,
          oauthRequestApi,
          configApi,
          provider: {
            id: 'oauth2',
            title: 'Ping SSO Auth',
            icon: SignInIcon,
          },
          defaultScopes: ['openid', 'profile', 'email'],
        }),
        configApi,
      );
    },
  }),
  createApiFactory({
    api: adServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, identityApi }) => new AdService(configApi.getString('backend.baseUrl'), identityApi),
  }),
  createApiFactory({
    api: AzureManagementServiceApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, oAuthApi, identityApi }) =>
      new AzureManagementService(
        `${configApi.getString('backend.baseUrl')}/api/proxy${configApi.getString('amService.proxyPath')}`,
        oAuthApi,
        identityApi,
      ),
  }),
  createApiFactory({
    api: GithubEnterpriseClientApiGraphRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
    },
    factory: ({ configApi, githubAuthApi }) =>
      new GithubEnterpriseClientGraph(configApi.getString(`github.ghec.graphUrl`), githubAuthApi),
  }),
  createApiFactory({
    api: Squad360ServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ configApi, identityApi, discoveryApi, fetchApi }) =>
      new Squad360Service(configApi.getString('backend.baseUrl'), identityApi, discoveryApi, fetchApi),
  }),
  createApiFactory({
    api: projectsDetailsQueryApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
    },
    factory: ({ configApi, githubAuthApi }) =>
      new ProjectsDetails(configApi.getString(`github.ghec.graphUrl`), githubAuthApi),
  }),
  createApiFactory({
    api: unifiedGithubApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
      emuAuthApi: emuAuthApiRef,
      identityApi: identityApiRef,
      featureFlags: featureFlagsApiRef,
    },
    factory: ({ configApi, githubAuthApi, emuAuthApi, identityApi, featureFlags }) =>
      new UnifiedGithubService(githubAuthApi, emuAuthApi, identityApi, {
        apiUrl: configApi.getString('github.ghec.apiUrl'),
        graphUrl: configApi.getString('github.ghec.graphUrl'),
        emuOrgUrl: configApi.getOptionalString('catalog.emuOrgUrl'),
        ghecOrg: configApi.getOptionalString('github.ghecOrg') ?? 'AAInternal',
        featureFlags,
      }),
  }),
  createApiFactory({
    api: githubAuthApiRef,
    deps: {
      configApi: configApiRef,
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
    },
    factory: ({ configApi, discoveryApi, oauthRequestApi }) => {
      const overrideToken = configApi.getOptionalString('frontendOverrides.githubAuthToken');
      const isMockEnabled = configApi.getOptionalString('frontendOverrides.enableGithubUserPATAuth');
      const githubUrl = configApi.getString('github.ghec.apiUrl');

      if (isMockEnabled === 'yes') {
        const mockAuth = new MockGithubAuthApi('githubSession');

        if (overrideToken !== undefined) {
          mockAuth.setAccessTokenInSession(overrideToken, githubUrl);
        }

        return mockAuth;
      }

      // remove cached token from previous GitHub OAuth app login
      localStorage.removeItem('githubSession');

      return GithubAuth.create({
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'github',
          title: 'Click here to authorize github.com access',
          icon: SignInIcon,
        },
        defaultScopes: ['repo', 'user:email', 'delete_repo', 'workflow'],
      });
    },
  }),
  // Factory for the GitHub EMU (Enterprise Managed Users) OAuth provider.
  // Pairs with the 'emu' provider entry in auth.providers of app-config.
  createApiFactory({
    api: emuAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
      const isMockEnabled = configApi.getOptionalString('frontendOverrides.enableGithubUserPATAuth');
      if (isMockEnabled === 'yes') {
        return new MockEmuGithubAuthAdapter();
      }

      const githubAuth = GithubAuth.create({
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'emu',
          title: 'GitHub EMU',
          icon: () => null,
        },
        defaultScopes: ['repo', 'user:email', 'delete_repo', 'workflow'],
        environment: configApi.getOptionalString('auth.environment'),
      });
      return new EmuGithubAuthAdapter(githubAuth);
    },
  }),
  createApiFactory({
    api: NotificationServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new NotificationService(configApi.getString('backend.baseUrl'), restClient, identityApi),
  }),
  createApiFactory({
    api: SecretVaultServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new SecretVaultService(configApi.getString('secretVault.svsUrl'), identityApi, restClient),
  }),
  createApiFactory({
    api: ADOServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClientApi: restClientRef,
    },
    factory: ({ configApi, identityApi, restClientApi }) => {
      const backendUrl = configApi.getString('backend.baseUrl');
      return new ADOService(backendUrl, restClientApi, identityApi);
    },
  }),
  createApiFactory({
    api: AIProvisioningServiceApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
      oneTrustApi: AIOneTrustServiceApiRef,
    },
    factory: ({ configApi, githubAuthApi, oneTrustApi }) =>
      new AIProvisioningService(configApi.getString('github.ghec.apiUrl'), githubAuthApi, oneTrustApi),
  }),
  createApiFactory({
    api: AIOneTrustServiceApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
    },
    factory: ({ configApi, githubAuthApi }) =>
      new AIOneTrustService(configApi.getString('github.ghec.apiUrl'), githubAuthApi),
  }),
  createApiFactory({
    api: AIAzureOnboardingServiceApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
    },
    factory: ({ configApi, githubAuthApi }) =>
      new AIAzureOnboardingService(configApi.getString('github.ghec.apiUrl'), githubAuthApi),
  }),
  createApiFactory({
    api: AIResourcesDeletionServiceApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ configApi, githubAuthApi, discoveryApi, fetchApi }) =>
      new AIResourcesDeletionService(configApi.getString('github.ghec.apiUrl'), githubAuthApi, discoveryApi, fetchApi),
  }),
  createApiFactory({
    api: OneTrustAssessmentServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new OneTrustAssessmentService(`${configApi.getString('backend.baseUrl')}/api/onetrust`, restClient, identityApi),
  }),
];
